import React, { useState, useEffect, useCallback, useMemo } from "react";
import EmployeeRecord from "./EmployeeRecord.js";
import ClaimLeaveHistoryTable from "./ClaimLeaveHistoryTable";
import {
    useGetEmployeeClaimDetailsQuery,
    useGetEmployeeDetailsQuery,
    useGetEmployeeLeaveDetailsQuery,
    useGetEmployeesQuery,
} from "./../../slices/employeesApiSlice";
import {
    useLocation,
    useParams,
    useSearchParams,
    useNavigate,
} from "react-router-dom";
import moment from "moment";
import LeaveRecord from "./LeaveRecord";
import FooterNote from "./FooterNote";
import ClaimRecord from "./ClaimRecord";
import { recordsType } from "../../constants/index";
import { useSelector, useDispatch } from "react-redux";
import {
    addLeaveNo,
    addClaimNo,
    resetRelatedNos,
} from "./../../slices/leaveClaimRecordsSlice";
import { persist } from "./../../slices/historySlice";
import { Breadcrumb } from "../../components/ui/Breadcrumb.js";
import Loader from "../../components/ui/Loader.js";
import ErrorBoundary from "../../components/ErrorBoundary.js";
import NoRecords from "../../components/alerts/NoRecords.js";
import { DataToPrint } from "../../components/print/DataToPrint.js";
import { useReactToPrint } from "react-to-print";
import { logout } from "./../../slices/authSlice";
import { ssnAccess } from "../../constants";
import RequestFailed from "../../components/alerts/RequestFailed";

import {
    addLastSelectedEmployee,
    addLastSelectedOrganization,
    addSelectedOrganization,
    setCurrentSelectedOrganization,
} from "../../slices/organizationSlice.js";
import GTM from "../../GTM.js";
import PropTypes from "prop-types";
import { useFlags } from "launchdarkly-react-client-sdk";
import { buildGtmOrgContext } from "../../utils/helpers.js";
import OrganizationRepository from "../../repositories/OrganizationRepository.js";
import OrganizationAccess from "../../models/OrganizationAccess.js";
import { setOrganisationPreference } from "../../slices/preferenceSlice.js";
import {
    addSelectedOrganizationForReports,
    setSelectedOrgainzationsIdForReports,
} from "../../slices/viewReportSlice.js";
import { setSelectedOdsEmployeeId } from "../../slices/employeeClaimLeaveSlice.js";

function EmployeeClaimLeave(props) {
    const { orgId, id } = useParams();
    const { user } = useSelector((state) => state.auth);
    const { config, flags } = useSelector((state) => state.app);
    const isODSEnabled = flags?.isODSEnabled ?? false;
    const { displayReports } = useFlags();
    const { selectedOrganizationsIdReports: selectedReportId } = useSelector(
        (state) => state.reportOrganization
    );
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const { selectedOdsEmployee } = useSelector(
        (state) => state.employeeClaimLeave
    );

    const {
        selectedOrganizations: userSelectedOrganizations,
        organizationsAccess,
    } = useSelector((state) => state.organizations);

    const url = window.location.pathname + window.location.search;

    useEffect(() => {
        // ODS uses UUID-format org IDs (not numeric), so skip the numeric check when ODS is enabled
        if (!isODSEnabled && isNaN(orgId)) {
            navigate(`/`, { replace: true });
            return;
        }

        dispatch(
            addLastSelectedEmployee({
                organizationId: orgId,
                path: url,
            })
        );
    }, [dispatch, navigate, isODSEnabled, orgId, url]);

    const gtm = new GTM(null);
    const [searchParams] = useSearchParams();
    const claimNo = searchParams.get("claim");
    const leaveNo = searchParams.get("leave");
    const claimMode = Boolean(claimNo);
    const leaveMode = Boolean(leaveNo);

    const getOrganizationIds = useCallback(() => {
        const ids = searchParams.get("ids");

        if (ids && typeof ids === "string" && ids.length > 0) {
            return ids;
        }
        return orgId;
    }, [orgId, searchParams]);

    const odsEmployeePreloaded = Boolean(
        location.state?.odsEmployeeHistory || selectedOdsEmployee
    );
    // Report hyperlinks open in a new tab without router state, so look the employee up here.
    const hasEmployeeId = id !== "view";
    const odsLookupSkipped =
        !isODSEnabled ||
        odsEmployeePreloaded ||
        (!hasEmployeeId && !leaveNo && !claimNo);
    const {
        currentData: odsLookupData,
        isSuccess: odsLookupSuccess,
        isLoading: odsEmployeeLoading,
        isFetching: odsEmployeeFetching,
        isUninitialized: odsEmployeeUninitialized,
        isError: odsEmployeeIsError,
        error: odsEmployeeError,
    } = useGetEmployeesQuery(
        {
            organizationId: getOrganizationIds(),
            filters: hasEmployeeId
                ? { eeid: id }
                : { leaveNumber: leaveNo ?? "", claimNumber: claimNo ?? "" },
        },
        { skip: odsLookupSkipped }
    );
    const odsEmployeeResolved =
        odsLookupSkipped || odsLookupSuccess || odsEmployeeIsError;

    const {
        data: apiEmployee = null,
        isLoading: apiIsLoading,
        isFetching: apiIsFetching,
        isUninitialized: apiIsUninitialized,
        isError: apiIsError,
        error: apiError,
    } = useGetEmployeeDetailsQuery(
        {
            employeeId: id,
            organizationId: getOrganizationIds(),
        },
        { skip: isODSEnabled }
    );

    const {
        data: claimDetailsData = null,
        isFetching: claimIsFetching,
        isUninitialized: claimIsUninitialized,
        isError: claimIsError,
        error: claimError,
    } = useGetEmployeeClaimDetailsQuery(
        {
            claimNo,
            employeeId: id,
            organizationId: isODSEnabled ? getOrganizationIds() : orgId,
            claimSystemCd: searchParams.get("claimSystem") || null,
        },
        {
            skip:
                !claimMode ||
                !odsEmployeeResolved ||
                (!isODSEnabled && !apiEmployee),
        }
    );

    const {
        data: leaveDetailsData = null,
        isFetching: leaveIsFetching,
        isUninitialized: leaveIsUninitialized,
        isError: leaveIsError,
        error: leaveError,
    } = useGetEmployeeLeaveDetailsQuery(
        {
            leaveNo,
            employeeId: id,
            organizationId: isODSEnabled ? getOrganizationIds() : orgId,
        },
        {
            skip:
                !leaveMode ||
                !odsEmployeeResolved ||
                (!isODSEnabled && !apiEmployee),
        }
    );

    useEffect(() => {
        const employeeKey = odsLookupData?.[0]?.MasterPartyId;
        if (employeeKey) {
            dispatch(setSelectedOdsEmployeeId(employeeKey));
        }
    }, [dispatch, odsLookupData]);

    const employee = useMemo(() => {
        if (isODSEnabled) {
            return (
                location.state?.odsEmployeeHistory ??
                selectedOdsEmployee ??
                null
            );
        }

        return apiEmployee;
    }, [apiEmployee, isODSEnabled, location.state, selectedOdsEmployee]);

    const activeLoading = isODSEnabled ? odsEmployeeLoading : apiIsLoading;
    const activeFetching = isODSEnabled ? odsEmployeeFetching : apiIsFetching;
    const activeUninitialized = isODSEnabled
        ? odsEmployeeUninitialized
        : apiIsUninitialized;
    const activeError = isODSEnabled
        ? odsEmployeeIsError
        : claimMode
          ? claimIsError
          : leaveMode
            ? leaveIsError
            : apiIsError;
    const activeQueryError = isODSEnabled
        ? odsEmployeeError
        : claimMode
          ? claimError
          : leaveMode
            ? leaveError
            : apiError;

    let previousUrls = props.previousUrl;
    let page_referrer = previousUrls[previousUrls.length - 1];
    const fallbackEmployeeOrgId =
        searchParams.get("ids")?.split(",")?.[0] || orgId;
    const claimParam = searchParams.get("claim");
    const leaveParam = searchParams.get("leave");
    const baseEmployeeSearchPath =
        [...previousUrls]
            .reverse()
            .find((path) =>
                /\/organization\/[^/]+\/employees\/search$/.test(path)
            ) || `/organization/${fallbackEmployeeOrgId}/employees/search`;
    const hasPreviousEmployeeSearch = [...previousUrls]
        .reverse()
        .some((path) => /\/organization\/[^/]+\/employees\/search$/.test(path));
    const employeeSearchPath =
        !hasPreviousEmployeeSearch && claimParam
            ? `${baseEmployeeSearchPath}?filterType=claimNumber&filterValue=${encodeURIComponent(claimParam)}`
            : !hasPreviousEmployeeSearch && leaveParam
              ? `${baseEmployeeSearchPath}?filterType=leaveNumber&filterValue=${encodeURIComponent(leaveParam)}`
              : baseEmployeeSearchPath;
    let page_referrer_title = "";

    if (page_referrer === "/search") {
        page_referrer_title = "search";
    } else {
        page_referrer_title = "employee search";
    }

    useEffect(() => {
        if (hasPreviousEmployeeSearch) {
            return;
        }

        const filters = {
            firstName: "",
            lastName: "",
            claimNumber: claimMode ? (claimNo ?? "") : "",
            leaveNumber: leaveMode ? (leaveNo ?? "") : "",
            eeid: !claimMode && !leaveMode ? id : "",
            ssn: "",
        };

        const requestedOrganizationIds = getOrganizationIds()
            .split(",")
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean);

        const breadcrumbSelectedRelatedOrganizations = (
            userSelectedOrganizations[orgId]?.related || []
        )
            .filter((organization) => {
                const isfId = String(
                    organization?.ORGANIZATIONIDISF || ""
                ).toLowerCase();
                const numericId = String(
                    organization?.ORGANIZATIONID || ""
                ).toLowerCase();

                return (
                    requestedOrganizationIds.includes(isfId) ||
                    requestedOrganizationIds.includes(numericId)
                );
            })
            .map((organization) => ({
                label: organization.ORGNIZATIONNAME,
                value: organization.ORGANIZATIONID,
                organizationIDISF: organization.ORGANIZATIONIDISF,
                organizationGroupId: organization.ORGANIZATIONGROUPID,
            }));

        dispatch(
            persist({
                key: baseEmployeeSearchPath,
                filters,
                selectedRelatedOrganizations:
                    breadcrumbSelectedRelatedOrganizations,
                organizationId: getOrganizationIds(),
            })
        );
    }, [
        claimMode,
        claimNo,
        dispatch,
        employeeSearchPath,
        getOrganizationIds,
        hasPreviousEmployeeSearch,
        id,
        leaveMode,
        leaveNo,
        orgId,
        userSelectedOrganizations,
    ]);

    useEffect(() => {
        const userId = user ? user.userId : "";
        const userRole = user.isUserInternal === true ? "internal" : "external";
        const userEmail = user.userEmail || "";
        const { employerName, relatedOrgNames, organizationGroupId } =
            buildGtmOrgContext(userSelectedOrganizations, selectedReportId);

        gtm.PageView(
            "claim & leave",
            employerName,
            userId,
            userRole,
            window.location.href,
            `${config.REACT_APP_BASE_URL + page_referrer}`,
            page_referrer_title,
            orgId,
            userEmail,
            relatedOrgNames,
            organizationGroupId,
            displayReports
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(setCurrentSelectedOrganization(orgId));
    }, [dispatch, orgId]);

    const requestedOrgIdsParam = searchParams.get("ids");

    // The URL org segment is not always the ODS org id, so fall back to "ids"
    // when it names a single unambiguous organization.
    const orgIdCandidates = useMemo(() => {
        const fallbackIds = (requestedOrgIdsParam?.split(",") ?? [])
            .map((value) => value.trim())
            .filter(Boolean);

        return [orgId, ...(fallbackIds.length === 1 ? fallbackIds : [])]
            .map((value) =>
                String(value ?? "")
                    .trim()
                    .toLowerCase()
            )
            .filter(Boolean);
    }, [orgId, requestedOrgIdsParam]);

    const organizationAccess = useMemo(
        () =>
            OrganizationAccess.findByOrganizationId(
                organizationsAccess,
                orgIdCandidates
            ),
        [organizationsAccess, orgIdCandidates]
    );

    const ssnDisplay = organizationAccess?.SSNACCESS ?? "";

    const { organizations: allOrganizations, parentOrganizations } =
        useSelector((state) => state.userDetails);

    // Set Organisation in the redux store when user is redirected to this page directly
    useEffect(() => {
        const isAlreadySelected = orgIdCandidates.some(
            (candidate) => candidate in userSelectedOrganizations
        );

        if (!isAlreadySelected) {
            const organization = parentOrganizations.find((org) => {
                return orgIdCandidates.includes(org.ORGANIZATIONIDISF);
            });

            if (organization) {
                selectOrganization(organization);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSelectedOrganizations, parentOrganizations, orgIdCandidates]);

    const selectOrganization = (organization) => {
        const related = OrganizationRepository.getRelatedOrganizations(
            allOrganizations,
            organization
        );

        dispatch(
            addSelectedOrganization({
                id: organization.ORGANIZATIONID,
                organization: organization,
                related,
            })
        );

        dispatch(addLastSelectedOrganization(organization));
        dispatch(
            setOrganisationPreference({
                organizationPreference: [organization.ORGANIZATIONIDISF],
            })
        );

        dispatch(
            addSelectedOrganizationForReports({
                id: organization.ORGANIZATIONID,
                organization: organization,
                related: related,
            })
        );

        dispatch(
            setSelectedOrgainzationsIdForReports(organization.ORGANIZATIONID)
        );
    };

    const [claimHistoryData, setClaimHistoryData] = useState([]);
    const [currentSSNState, setCurrentSSNState] = useState(
        location.state?.ssnState
    );

    useEffect(() => {
        if (
            !isODSEnabled &&
            activeQueryError &&
            activeQueryError.status === 401
        ) {
            dispatch(logout());
        }
    }, [activeQueryError, dispatch, isODSEnabled]);

    const [employeesLastFetched, setEmployeesLastFetched] = useState("-");

    const [orgIdNo, setOrgIdNo] = useState(null);

    const handleSelectedOrgId = useCallback((orgId) => {
        setOrgIdNo(orgId);
    }, []);

    const handleLeaveNoSelection = useCallback(
        (leaveNo) => {
            dispatch(addLeaveNo(leaveNo));
        },
        [dispatch]
    );

    const handleClaimNoSelection = useCallback(
        (claimNo) => {
            dispatch(addClaimNo(claimNo));
        },
        [dispatch]
    );

    useEffect(() => {
        if (!activeLoading) {
            const claim = searchParams.get("claim");
            const leave = searchParams.get("leave");

            if (claim) {
                dispatch(addClaimNo(claim));
            }

            if (leave) {
                dispatch(addLeaveNo(leave));
            }
        }
    }, [activeLoading, dispatch, searchParams]);

    const claimSystemCd = searchParams.get("claimSystem") || null;

    const [printing, setPrinting] = React.useState(false);
    const componentRef = React.useRef();

    const print = useReactToPrint({
        content: () => componentRef.current,
        onAfterPrint: () => {
            setPrinting(false);
        },
    });

    useEffect(() => {
        if (printing === true) {
            setTimeout(() => {
                print();
            }, 200);
        }
    }, [printing, print]);

    const onSSNClick = useCallback(
        (ssnState) => {
            let newSSNState = "TRUNCATED";

            if (ssnState === "NORMAL") newSSNState = "HIDDEN";
            else if (ssnState === "TRUNCATED") newSSNState = "NORMAL";

            if (ssnDisplay === ssnAccess.Partial && newSSNState === "NORMAL") {
                newSSNState = "HIDDEN";
            }

            setCurrentSSNState(newSSNState);
        },
        [ssnDisplay]
    );

    const { selectedNo, type, lastUpdatedId, selectedRecords } = useSelector(
        (state) => state.leaveClaimRecords
    );

    useEffect(() => {
        setEmployeesLastFetched(moment().format("MM/DD/YYYY hh:mm A"));
    }, [activeLoading, lastUpdatedId.id]);

    useEffect(() => {
        if (
            (claimMode || leaveMode) &&
            Array.isArray(employee?.ClaimLeaveData)
        ) {
            setClaimHistoryData(employee.ClaimLeaveData);
        }
    }, [claimMode, employee, leaveMode]);

    useEffect(() => {
        return () => {
            dispatch(resetRelatedNos());
        };
    }, [dispatch]);

    const initPrint = useCallback(() => {
        setPrinting(true);
    }, []);

    useEffect(() => {
        if (lastUpdatedId.id !== "") {
            setTimeout(() => {
                const section = document.getElementById(lastUpdatedId.id);

                window.scrollTo({
                    top: section.offsetTop - 270,
                    behavior: "smooth",
                });
            }, [200]);
        }
    }, [lastUpdatedId]);

    const handleClaimHistoryData = useCallback((data) => {
        setClaimHistoryData(data);
    }, []);

    const [isFetchingLeave, setIsFetchingLeave] = useState(false);

    let fetchingLeave = (value) => {
        setIsFetchingLeave(value);
    };

    const [isFetchingClaim, setIsFetchingClaim] = useState(false);

    let fetchingClaim = (value) => {
        setIsFetchingClaim(value);
    };

    return (
        <ErrorBoundary>
            <div className="employeedet">
                <Breadcrumb
                    links={[
                        {
                            path: "/search",
                            name: "Search",
                        },
                        {
                            path: employeeSearchPath,
                            name: "Employee",
                        },
                        {
                            name: "Claim & Leave",
                        },
                    ]}
                />

                {activeFetching && <Loader />}

                {!activeUninitialized &&
                    !activeFetching &&
                    !activeError &&
                    (!employee || employee?.EmployeeDetails === null) && (
                        <NoRecords
                            page="claim & leave"
                            label="claim & leave"
                            attribute1="claim & leave"
                            attribute3="claim_error"
                        />
                    )}
                {activeError && (
                    <RequestFailed
                        tabIndex={0}
                        message={
                            "Your search failed, please try again or contact us for further assistance."
                        }
                        label="claim & leave"
                        attribute1="claim & leave"
                        attribute3="claim_error"
                    />
                )}
                {employee?.EmployeeDetails != null && !activeFetching && (
                    <>
                        <EmployeeRecord
                            employee={employee.EmployeeDetails}
                            isLoading={activeLoading}
                            ssnDisplay={ssnDisplay}
                            ssnState={currentSSNState}
                            initPrint={initPrint}
                            onSSNClick={onSSNClick}
                            hideBanner={props.showInformationBanner}
                            isFetchingLeave={
                                leaveMode ? leaveIsFetching : isFetchingLeave
                            }
                            isFetchingClaim={
                                claimMode ? claimIsFetching : isFetchingClaim
                            }
                        />

                        <ClaimLeaveHistoryTable
                            handleLeaveNoSelection={handleLeaveNoSelection}
                            handleClaimNoSelection={handleClaimNoSelection}
                            handleClaimHistoryData={handleClaimHistoryData}
                            handleSelectedOrgId={handleSelectedOrgId}
                            employeeLeaves={employee.ClaimLeaveData}
                            isLoading={activeLoading}
                        />
                    </>
                )}

                {!activeFetching && (
                    <>
                        {type === recordsType.LEAVE &&
                            selectedNo !== null &&
                            (leaveMode &&
                            selectedNo === leaveNo &&
                            leaveIsFetching ? (
                                <Loader />
                            ) : (
                                <LeaveRecord
                                    leaveNo={selectedNo}
                                    employeeId={id}
                                    organizationId={
                                        orgIdNo === null ? orgId : orgIdNo
                                    }
                                    organizationAccess={organizationAccess}
                                    fetchingLeave={fetchingLeave}
                                    user={user}
                                    leaveDetailsData={
                                        leaveMode && selectedNo === leaveNo
                                            ? leaveDetailsData
                                            : null
                                    }
                                    skipQuery={
                                        leaveMode && selectedNo === leaveNo
                                    }
                                />
                            ))}

                        {type === recordsType.CLAIM &&
                            selectedNo !== null &&
                            (claimMode &&
                            selectedNo === claimNo &&
                            claimIsFetching ? (
                                <Loader />
                            ) : (
                                <ClaimRecord
                                    claimNo={selectedNo}
                                    employeeId={id}
                                    organizationId={
                                        orgIdNo === null ? orgId : orgIdNo
                                    }
                                    organizationAccess={organizationAccess}
                                    fetchingClaim={fetchingClaim}
                                    user={user}
                                    claimSystemCd={claimSystemCd}
                                    claimDetailsData={
                                        claimMode && selectedNo === claimNo
                                            ? claimDetailsData
                                            : null
                                    }
                                    skipQuery={
                                        claimMode && selectedNo === claimNo
                                    }
                                />
                            ))}

                        {selectedRecords.map((selectedRecord) => {
                            if (selectedRecord.type === recordsType.LEAVE) {
                                return (
                                    <LeaveRecord
                                        key={selectedRecord?.no}
                                        leaveNo={selectedRecord.no}
                                        employeeId={id}
                                        organizationId={
                                            orgIdNo === null ? orgId : orgIdNo
                                        }
                                        organizationAccess={organizationAccess}
                                        fetchingLeave={fetchingLeave}
                                        user={user}
                                    />
                                );
                            } else if (
                                selectedRecord.type === recordsType.CLAIM
                            ) {
                                return (
                                    <ClaimRecord
                                        key={selectedRecord?.no}
                                        claimNo={selectedRecord.no}
                                        employeeId={id}
                                        organizationId={
                                            orgIdNo === null ? orgId : orgIdNo
                                        }
                                        organizationAccess={organizationAccess}
                                        fetchingClaim={fetchingClaim}
                                        user={user}
                                        claimSystemCd={claimSystemCd}
                                    />
                                );
                            }

                            return null;
                        })}
                    </>
                )}

                {!activeLoading && <FooterNote date={employeesLastFetched} />}
            </div>

            <DataToPrint
                ref={componentRef}
                printing={printing}
                data={{
                    employee: employee,
                    ssnDisplay: ssnDisplay,
                    ssnState: currentSSNState,
                    employeeLeaves: employee ? employee.ClaimLeaveData : [],
                    employeesLastFetched: employeesLastFetched,
                    employeeId: id,
                    orgId: orgIdNo === null ? orgId : orgIdNo,
                    organizationsAccess: organizationsAccess,
                    claimHistoryData: claimHistoryData,
                }}
            />
        </ErrorBoundary>
    );
}

EmployeeClaimLeave.propTypes = {
    previousUrl: PropTypes.arrayOf(PropTypes.string),
    showInformationBanner: PropTypes.bool,
};

export default React.memo(EmployeeClaimLeave);
